import { useCallback, useMemo, useState } from 'react'
import { atom, useAtom } from 'jotai'
import { PAGINATION_LIMIT } from '@renderer/utils/constants'
import { useSharedItems } from '@renderer/hooks/shared-items'

export const sharedItemsPageAtom = atom(0)

export function useSharedWithMe() {
  const [localPage, setLocalPage] = useAtom(sharedItemsPageAtom)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sharedItemsQuery = useSharedItems()

  // Transform shared items to match the format expected by the UI
  const combinedData = useMemo(() => {
    if (!sharedItemsQuery.data) return []

    const transformed = sharedItemsQuery.data
      .map((item) => {
        if (item.type === 'audio_note') {
          return {
            ...item.content,
            unifiedUpdatedAt: new Date(item.sharedAt),
            type: 'audio-note' as const,
            sharedBy: item.sharedBy,
            isShared: true,
          }
        }
        return null
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)

    return transformed
  }, [sharedItemsQuery.data])

  const fetchNextPage = useCallback(async () => {
    if (isLoadingMore || !combinedData) return

    try {
      setIsLoadingMore(true)

      const currentEnd = (localPage + 1) * PAGINATION_LIMIT
      const nextBatchOfItems = combinedData.slice(currentEnd, currentEnd + PAGINATION_LIMIT)

      // Check if we have enough items in memory already
      if (nextBatchOfItems.length >= PAGINATION_LIMIT) {
        setLocalPage((prev) => prev + 1)
        return
      }

      // Fetch more data if needed
      if (sharedItemsQuery.hasNextPage) {
        await sharedItemsQuery.fetchNextPage()
      }

      setLocalPage((prev) => prev + 1)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, combinedData, localPage, sharedItemsQuery, setLocalPage])

  const visibleData = useMemo(() => {
    if (!combinedData) return null
    return combinedData.slice(0, (localPage + 1) * PAGINATION_LIMIT)
  }, [combinedData, localPage])

  const hasNextPage = useMemo(() => {
    if (!combinedData) return false
    return combinedData.length > (localPage + 1) * PAGINATION_LIMIT || sharedItemsQuery.hasNextPage
  }, [combinedData, localPage, sharedItemsQuery.hasNextPage])

  return useMemo(() => {
    return {
      data: visibleData,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage: isLoadingMore,
      isLoading: sharedItemsQuery.isLoading,
      sharedItemsQuery,
    }
  }, [visibleData, hasNextPage, fetchNextPage, sharedItemsQuery, isLoadingMore])
}
